import { useRef, useState, useCallback, useEffect, type MouseEvent as ReactMouseEvent } from 'react';

/* ── Physics constants (iOS-feel) ── */
const VELOCITY_THRESHOLD = 0.3; // px/ms — flick detection
const SNAP_FRACTION = 0.25; // drag 25% of width → snap
const RUBBER_FACTOR = 0.3; // overscroll resistance
const SPRING_DURATION = 450; // ms snap animation
const SPRING_BEZIER = 'cubic-bezier(0.25, 1, 0.5, 1)';

/**
 * #486 — visual-regression determinism.
 *
 * In production we want the random-start UX (so the same user doesn't
 * always see Match Result first when popping back into an article).
 * Under Storybook's preview iframe — which Playwright drives for the
 * visual-regression CI — that randomness makes two back-to-back
 * screenshots disagree and the snapshot comparison times out.
 *
 * Pin idx=0 ONLY inside Storybook's preview iframe. Checks two signals
 * (defensive): the `__STORYBOOK_PREVIEW__` global Storybook 8 sets on
 * the preview window, and the `/iframe.html` URL fallback for older
 * Storybook versions or non-standard hosting. Both off → production.
 */
function isStorybookPreviewEnv(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as Window & { __STORYBOOK_PREVIEW__?: unknown };
  if (w.__STORYBOOK_PREVIEW__ !== undefined) return true;
  return window.location?.pathname?.endsWith('/iframe.html') === true;
}

interface TouchSample {
  x: number;
  t: number;
}

function computeVelocity(samples: TouchSample[]): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1];
  let ref = samples[0];
  if (!last || !ref) return 0;
  for (let i = samples.length - 2; i >= 0; i--) {
    const s = samples[i];
    if (s && last.t - s.t >= 60) {
      ref = s;
      break;
    }
  }
  const dt = last.t - ref.t;
  return dt === 0 ? 0 : (last.x - ref.x) / dt;
}

interface UseSwipeCarouselOptions {
  count: number;
  autoInterval?: number;
  pauseAfterInteraction?: number;
  loop?: boolean;
}

interface UseSwipeCarouselResult {
  activeIdx: number;
  trackRef: React.RefObject<HTMLDivElement | null>;
  viewportRef: React.RefObject<HTMLDivElement | null>;
  paused: boolean;
  setPaused: (paused: boolean) => void;
  goTo: (idx: number) => void;
  advance: (dir: 1 | -1) => void;
  blockLinkIfSwiped: (e: ReactMouseEvent) => void;
  didSwipe: React.MutableRefObject<boolean>;
}

export function useSwipeCarousel({
  count,
  autoInterval = 0,
  pauseAfterInteraction = 4000,
  loop = true,
}: UseSwipeCarouselOptions): UseSwipeCarouselResult {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const trackRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const offsetX = useRef(0);
  const dragStartX = useRef(0);
  const dragOffsetAtStart = useRef(0);
  const isDragging = useRef(false);
  const didSwipe = useRef(false);
  const isHorizontal = useRef<boolean | null>(null);
  const touchSamples = useRef<TouchSample[]>([]);
  const slideWidth = useRef(0);
  const startY = useRef(0);
  const activeIdxRef = useRef(0);
  const countRef = useRef(count);
  countRef.current = count;

  /* ── Measure slide width from viewport (not wrapper — avoids padding mismatch) ── */
  const measure = useCallback((): void => {
    if (viewportRef.current) {
      slideWidth.current = viewportRef.current.offsetWidth;
    }
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return (): void => window.removeEventListener('resize', measure);
  }, [measure]);

  /* ── Apply transform ── */
  const applyTransform = useCallback((x: number, animated = false): void => {
    if (!trackRef.current) return;
    trackRef.current.style.transition = animated
      ? `transform ${SPRING_DURATION}ms ${SPRING_BEZIER}`
      : 'none';
    trackRef.current.style.transform = `translate3d(${x}px, 0, 0)`;
    offsetX.current = x;
  }, []);

  /* ── Snap to index ── */
  const snapTo = useCallback(
    (idx: number, animated = true): void => {
      const clamped = Math.max(0, Math.min(idx, countRef.current - 1));
      applyTransform(-clamped * slideWidth.current, animated);
      setActiveIdx(clamped);
      activeIdxRef.current = clamped;
    },
    [applyTransform],
  );

  /* ── Navigate ── */
  const goTo = useCallback(
    (idx: number): void => {
      snapTo(idx);
    },
    [snapTo],
  );

  const advance = useCallback(
    (dir: 1 | -1): void => {
      let next = activeIdxRef.current + dir;
      if (loop) {
        next = ((next % countRef.current) + countRef.current) % countRef.current;
      } else {
        next = Math.max(0, Math.min(next, countRef.current - 1));
      }
      snapTo(next);
    },
    [loop, snapTo],
  );

  /* ── Auto-rotate ── */
  useEffect(() => {
    if (paused || count <= 1 || !autoInterval) return;
    const id = setInterval(() => advance(1), autoInterval);
    return (): void => clearInterval(id);
  }, [paused, count, autoInterval, advance]);

  /* ── Random start (production) / pinned idx=0 (Storybook preview) ── */
  useEffect(() => {
    if (count > 1) {
      // See `isStorybookPreviewEnv` comment at top of file (#486).
      const idx = isStorybookPreviewEnv() ? 0 : Math.floor(Math.random() * count);
      setActiveIdx(idx);
      activeIdxRef.current = idx;
      requestAnimationFrame(() => {
        measure();
        applyTransform(-idx * slideWidth.current, false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  /* ──────────────────────────────────────────────
     Native touch handlers (non-passive for preventDefault)
     React synthetic events are passive by default —
     that's what causes the console errors.
     We attach with addEventListener({ passive: false }).
     ────────────────────────────────────────────── */
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    function handleTouchStart(e: TouchEvent): void {
      // Interrupt any ongoing CSS transition
      if (trackRef.current) {
        const matrix = new DOMMatrix(getComputedStyle(trackRef.current).transform);
        applyTransform(matrix.m41, false);
      }

      const startTouch = e.touches[0];
      if (!startTouch) return;
      isDragging.current = true;
      isHorizontal.current = null;
      didSwipe.current = false;
      dragStartX.current = startTouch.clientX;
      startY.current = startTouch.clientY;
      dragOffsetAtStart.current = offsetX.current;
      touchSamples.current = [{ x: startTouch.clientX, t: Date.now() }];
      setPaused(true);
    }

    function handleTouchMove(e: TouchEvent): void {
      if (!isDragging.current) return;

      const moveTouch = e.touches[0];
      if (!moveTouch) return;
      const cx = moveTouch.clientX;
      const cy = moveTouch.clientY;

      // Lock direction on first significant movement
      if (isHorizontal.current === null) {
        const absDx = Math.abs(cx - dragStartX.current);
        const absDy = Math.abs(cy - startY.current);
        if (absDx > 8 || absDy > 8) {
          isHorizontal.current = absDx > absDy;
        }
      }

      if (isHorizontal.current === false) {
        isDragging.current = false;
        return;
      }

      // Block vertical scroll during horizontal swipe
      if (isHorizontal.current && e.cancelable) {
        e.preventDefault();
      }

      touchSamples.current.push({ x: cx, t: Date.now() });
      if (touchSamples.current.length > 10) touchSamples.current.shift();

      let rawDelta = cx - dragStartX.current;
      const proposed = dragOffsetAtStart.current + rawDelta;
      const minOff = -(countRef.current - 1) * slideWidth.current;

      // Rubberband at edges
      if (proposed > 0) {
        rawDelta = -dragOffsetAtStart.current + proposed * RUBBER_FACTOR;
      } else if (proposed < minOff) {
        const over = proposed - minOff;
        rawDelta = minOff - dragOffsetAtStart.current + over * RUBBER_FACTOR;
      }

      applyTransform(dragOffsetAtStart.current + rawDelta, false);
      didSwipe.current = Math.abs(rawDelta) > 5;
    }

    function handleTouchEnd(): void {
      if (!isDragging.current && !didSwipe.current) {
        setTimeout(() => setPaused(false), pauseAfterInteraction);
        return;
      }
      isDragging.current = false;

      const velocity = computeVelocity(touchSamples.current);
      const cw = slideWidth.current || 1;
      let naturalIdx = -offsetX.current / cw;

      if (Math.abs(velocity) > VELOCITY_THRESHOLD) {
        naturalIdx = velocity < 0 ? Math.ceil(naturalIdx) : Math.floor(naturalIdx);
      } else {
        const dragDelta = offsetX.current - dragOffsetAtStart.current;
        if (Math.abs(dragDelta) > cw * SNAP_FRACTION) {
          naturalIdx = dragDelta < 0 ? Math.ceil(naturalIdx) : Math.floor(naturalIdx);
        } else {
          naturalIdx = Math.round(naturalIdx);
        }
      }

      const target = Math.max(0, Math.min(Math.round(naturalIdx), countRef.current - 1));
      snapTo(target);
      setTimeout(() => setPaused(false), pauseAfterInteraction);
    }

    // Attach with { passive: false } so preventDefault() works
    track.addEventListener('touchstart', handleTouchStart, { passive: true });
    track.addEventListener('touchmove', handleTouchMove, { passive: false });
    track.addEventListener('touchend', handleTouchEnd, { passive: true });

    return (): void => {
      track.removeEventListener('touchstart', handleTouchStart);
      track.removeEventListener('touchmove', handleTouchMove);
      track.removeEventListener('touchend', handleTouchEnd);
    };
  }, [applyTransform, snapTo, pauseAfterInteraction]);

  /* ── Link click guard ── */
  const blockLinkIfSwiped = useCallback((e: ReactMouseEvent): void => {
    if (didSwipe.current) e.preventDefault();
  }, []);

  return {
    activeIdx,
    trackRef,
    viewportRef,
    paused,
    setPaused,
    goTo,
    advance,
    blockLinkIfSwiped,
    didSwipe,
  };
}
